import React, { useState, useEffect } from 'react'
import { Button, Card, Grid, IconButton, makeStyles, Typography } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { FieldArray, Form, Formik } from 'formik'
import { pollFormSchema } from 'schemas/polling-1'
import { Letters, PollStatus, SpeakerRoles } from 'constants/index'
import {
  addPollAlongWithQuestions,
  fetchPollById,
  resetPollById,
  updatePollAlongWithQuestions
} from 'store/polling'
import CustomTextField from 'components/Form/CustomTextField'
import { Box } from '@mui/material'
import { Add, AddCircle, Close, DoDisturbOnOutlined } from '@material-ui/icons'
import { nanoid } from 'nanoid'
import { useDispatch, useSelector } from 'react-redux'
import ProgressButton from 'components/ProgressButton'
import { colors } from 'utils/colors'
import Tooltip from '@material-ui/core/Tooltip'
import { toast } from 'utils/notify'
import TextField from '@material-ui/core/TextField'
import CustomPopup from 'components/CustomPopup'
import SpeakerForm from 'views/admin/speakers/SpeakerForm'
import { getModeratorsAndPresenters } from 'store/speaker'
import ConfirmModal from 'components/ConfirmationModal'
import DataLoading from 'components/DataLoading'

const CREATE_SPEAKER_OPTION_ID = '__create_speaker__'

const PollingForm = ({
  editedPollId,
  pollStatus,
  pollValues,
  speakerId,
  conferenceId,
  handleCloseModal,
  handleCloseConfirmModal,
  associateSpeaker,
  isCustomSubmit,
  handleCustomSubmit,
  reloadPolls,
  onSuccess
}) => {
  const dispatch = useDispatch()
  const classes = useStyles()
  const [loading, setLoading] = useState(false)
  const [resettingPoll, setResettingPoll] = useState(false)
  const [openResetPoll, setOpenResetPoll] = useState(false)
  const [loadingSpeakers, setLoadingSpeakers] = useState(false)
  const [speakers, setSpeakers] = useState([])
  const [openSpeakerModal, setOpenSpeakerModal] = useState(false)
  const { user } = useSelector((state) => state.auth)

  useEffect(() => {
    const fetchModeratorsAndPresenters = async () => {
      setLoadingSpeakers(true)
      const data = await getModeratorsAndPresenters(user?.conferenceId)
      setSpeakers(data.presenters || [])
      setLoadingSpeakers(false)
    }
    fetchModeratorsAndPresenters()
  }, [user?.conferenceId])

  const emptyQuestion = {
    title: '',
    options: [
      { optId: nanoid(), opt: '' },
      { optId: nanoid(), opt: '' }
    ]
  }

  const resetPoll = async () => {
    setResettingPoll(true)
    await dispatch(resetPollById(editedPollId))
    setResettingPoll(false)
  }

  const handleSubmit = async (values) => {
    setLoading(true)
    const updatedValues = {
      ...values,
      conferenceId,
      status: PollStatus.CREATED
    }
    if (!associateSpeaker && speakerId) {
      updatedValues.speakerId = speakerId
    }
    if (editedPollId) {
      const poll = await fetchPollById(editedPollId)
      if (poll.status === PollStatus.CLOSED) {
        handleCloseConfirmModal()
        toast.error('This poll has concluded and cannot be edited.')
        return
      } else if (poll.status !== PollStatus.CREATED) {
        handleCloseConfirmModal()
        toast.error('This poll is in progress and cannot be edited.')
        return
      }
      const missingQuestionIds = pollValues?.questions
        .filter(
          (initialQuestion) =>
            !updatedValues.questions.some(
              (updatedQuestion) => updatedQuestion.id === initialQuestion.id
            )
        )
        .map((question) => question.id)
      await dispatch(updatePollAlongWithQuestions(editedPollId, updatedValues, missingQuestionIds))
      toast.success('Poll and questions updated.')
    } else {
      const pollId = await dispatch(addPollAlongWithQuestions(updatedValues))
      reloadPolls && (await reloadPolls())
      if (onSuccess) {
        await onSuccess(pollId, values.speakerId)
      } else toast.success('Poll and questions saved.')
    }
    setLoading(false)
    handleCloseConfirmModal()
  }

  if (loadingSpeakers) {
    return (
      <Box sx={{ marginTop: '2rem' }}>
        <DataLoading />
      </Box>
    )
  }
  return (
    <Box className={classes.root}>
      <Formik
        enableReinitialize
        initialValues={associateSpeaker ? { ...pollValues, speakerId: '' } : pollValues}
        validationSchema={pollFormSchema({ associateSpeaker: associateSpeaker })}
        onSubmit={async (values) =>
          isCustomSubmit ? handleCustomSubmit(values) : await handleSubmit(values)
        }>
        {({ values, touched, errors, setFieldValue }) => (
          <Form className={classes.form}>
            <Box className={classes.modalHeader}>
              <Typography className={classes.modalTitle}>
                {editedPollId ? 'Edit Poll Details' : 'Add new Poll'}
              </Typography>
              <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                {editedPollId && pollStatus !== PollStatus.CREATED && (
                  <Button
                    variant="contained"
                    onClick={() => setOpenResetPoll(true)}
                    className={classes.resetPollBtn}>
                    Reset Poll
                  </Button>
                )}
                <Tooltip title="Close">
                  <IconButton className={classes.closeBtn} onClick={() => handleCloseModal(values)}>
                    <Close fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            <Box className={classes.scrollArea}>
              <Grid
                container
                spacing={2}
                className={
                  editedPollId && pollStatus !== PollStatus.CREATED ? classes.disabled : ''
                }>
                <Grid item xs={associateSpeaker ? 6 : 12}>
                  <CustomTextField
                    name="title"
                    type="text"
                    placeholder="Enter poll title here..."
                    label="Poll Title"
                    disabled={editedPollId && pollStatus !== PollStatus.CREATED}
                  />
                </Grid>
                {associateSpeaker && (
                  <Grid item xs={6}>
                    <Autocomplete
                      className={classes.selectSpeakerMenu}
                      fullWidth
                      disabled={editedPollId && pollStatus !== PollStatus.CREATED}
                      options={[
                        ...(speakers || []),
                        { id: CREATE_SPEAKER_OPTION_ID, isCreateOption: true }
                      ]}
                      value={speakers?.find((speaker) => speaker.id === values.speakerId) || null}
                      onChange={(event, newValue) => {
                        if (newValue?.isCreateOption) {
                          setOpenSpeakerModal(true)
                          return
                        }
                        setFieldValue('speakerId', newValue ? newValue.id : '')
                      }}
                      getOptionLabel={(speaker) =>
                        speaker?.isCreateOption ? 'Create speaker' : speaker?.fullName || ''
                      }
                      renderOption={(speaker) =>
                        speaker.isCreateOption ? (
                          <Typography className={classes.buttonItem}>
                            <Add />
                            Create speaker
                          </Typography>
                        ) : (
                          <Box className={classes.listItem}>{speaker.fullName}</Box>
                        )
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select a Speaker"
                          variant="outlined"
                          error={Boolean(touched.speakerId && errors.speakerId)}
                          helperText={
                            touched.speakerId && errors.speakerId ? errors.speakerId : null
                          }
                        />
                      )}
                    />
                  </Grid>
                )}
              </Grid>

              <Box
                className={
                  editedPollId && pollStatus !== PollStatus.CREATED ? classes.disabled : ''
                }>
                <Typography className={classes.questionHeader}>Poll Questions</Typography>
                <FieldArray name="questions">
                  {({ push, remove }) => (
                    <Box>
                      {values.questions?.map((question, index) => (
                        <Card elevation={0} className={classes.questionCard} key={index}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <CustomTextField
                              type="text"
                              name={`questions.${index}.title`}
                              label={`Question No ${index + 1}`}
                              placeholder="Write your question title here..."
                              disabled={editedPollId && pollStatus !== PollStatus.CREATED}
                            />
                            {values?.questions?.length > 1 && (
                              <Tooltip title="Delete Question">
                                <IconButton
                                  onClick={() => {
                                    remove(index)
                                  }}>
                                  <DoDisturbOnOutlined />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>

                          <FieldArray name={`questions.${index}.options`}>
                            {({ push: pushOption, remove: removeOption }) => (
                              <Box className={classes.questionOptions}>
                                {question?.options?.map((option, optionIndex) => (
                                  <Box key={optionIndex + option.optId}>
                                    <Box
                                      sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                      <Typography>{Letters[optionIndex]}.</Typography>
                                      <CustomTextField
                                        type="text"
                                        name={`questions.${index}.options.${optionIndex}.opt`}
                                        placeholder={`Option ${Letters[optionIndex]}`}
                                        disabled={editedPollId && pollStatus !== PollStatus.CREATED}
                                      />
                                      {question?.options?.length > 2 && (
                                        <Tooltip title="Delete Option">
                                          <IconButton
                                            onClick={() => {
                                              removeOption(optionIndex)
                                            }}>
                                            <DoDisturbOnOutlined />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </Box>
                                    {optionIndex < 6 &&
                                      optionIndex === question?.options?.length - 1 && (
                                        <Box sx={{ marginTop: '16px' }}>
                                          <Button
                                            className={classes.addBtn}
                                            color="primary"
                                            variant="contained"
                                            onClick={() => pushOption({ optId: nanoid(), opt: '' })}
                                            disabled={
                                              editedPollId && pollStatus !== PollStatus.CREATED
                                            }
                                            startIcon={<AddCircle />}>
                                            Add Option
                                          </Button>
                                        </Box>
                                      )}
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </FieldArray>
                        </Card>
                      ))}
                      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '16px' }}>
                        <Button
                          variant="contained"
                          className={classes.addQuestionBtn}
                          onClick={() => push(emptyQuestion)}
                          startIcon={<AddCircle />}
                          disabled={editedPollId && pollStatus !== PollStatus.CREATED}>
                          Add Question
                        </Button>
                      </Box>
                    </Box>
                  )}
                </FieldArray>
              </Box>
            </Box>

            <Box className={classes.pollBtnBox}>
              <Button disabled={loading} onClick={() => handleCloseModal(values)}>
                Cancel
              </Button>
              <ProgressButton
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading || (editedPollId && pollStatus !== PollStatus.CREATED)}
                label={editedPollId ? 'Update Poll' : 'Create Poll'}
                inProgress={loading}
              />
            </Box>
            <CustomPopup
              title="Create speaker"
              maxWidth="lg"
              open={openSpeakerModal}
              onClose={() => setOpenSpeakerModal(false)}>
              <SpeakerForm
                displayContext="modal"
                onCloseModal={() => setOpenSpeakerModal(false)}
                preDefinedRole={SpeakerRoles.PRESENTER}
                onSuccess={(speakerId) => {
                  setOpenSpeakerModal(false)
                  setFieldValue('speakerId', speakerId)
                }}
              />
            </CustomPopup>
            <ConfirmModal
              dialogTitle="Reset poll"
              dialogText="You're about to reset this poll, which may have responses to the questions. All existing answers will be lost. Are you sure you want to continue?"
              open={Boolean(openResetPoll)}
              closeModal={() => setOpenResetPoll(false)}
              handleConfirm={async () => {
                await resetPoll()
                handleCloseModal(values)
                toast.success('Poll reset.')
              }}
              inProgress={resettingPoll}
            />
          </Form>
        )}
      </Formik>
    </Box>
  )
}

export default PollingForm

const useStyles = makeStyles({
  root: {
    height: '100%'
  },
  form: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  scrollArea: {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    // Without an explicit overflowX, the browser auto-promotes it to 'auto' as well (CSS
    // overflow spec), which turns the Grid's few-px negative-margin bleed into a visible
    // horizontal scrollbar here.
    overflowX: 'hidden',
    // Right padding keeps the scrollbar from sitting flush against the field borders.
    padding: '8px 20px 8px 4px'
  },
  selectSpeakerMenu: {
    '& .MuiOutlinedInput-root': {
      '& .MuiSelect-outlined.MuiSelect-outlined': {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem'
      }
    }
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem'
  },
  modalHeader: {
    paddingTop: '16px',
    paddingBottom: '10px',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
    background: 'white'
  },
  disabled: {
    pointerEvents: 'none',
    opacity: 0.7
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: 'bold'
  },
  closeBtn: {
    padding: '10px'
  },
  addBtn: {
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 'bold'
  },
  resetPollBtn: {
    color: 'white',
    fontWeight: 'bold',
    letterSpacing: '0.5px',
    backgroundColor: '#d32f2f',
    '&:hover': {
      backgroundColor: '#c62828'
    }
  },
  addQuestionBtn: {
    textTransform: 'none',
    color: 'white',
    fontWeight: 'bold',
    backgroundColor: '#fb8c00',
    '&:hover': {
      backgroundColor: '#ef6c00'
    }
  },
  progressBtnGrid: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  pollBtnBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    justifyContent: 'flex-end',
    flexShrink: 0,
    background: 'white',
    borderTop: '1px solid #eeeeee',
    marginTop: '16px',
    padding: '16px 0'
  },
  questionOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '700px',
    paddingLeft: '30px'
  },
  questionHeader: {
    marginTop: '16px',
    fontWeight: 'bold',
    fontSize: '17px',
    color: colors.indigo
  },
  questionCard: {
    marginTop: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    padding: '16px',
    border: '1px solid #bdbdbd',
    borderRadius: '10px'
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem'
  },
  buttonItem: {
    color: colors.indigo,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '0.3rem'
  }
})
